import { defineComponent, ref, computed, watch, nextTick, onMounted, onUnmounted, provide, toRef, openBlock, createElementBlock, Fragment, withDirectives, normalizeStyle, renderSlot, unref, createBlock, Teleport, createVNode, Transition, withCtx, normalizeClass, createElementVNode, withModifiers, isRef, vModelText, createCommentVNode, createTextVNode, toDisplayString } from "vue";
import { offset, flip, shift, size, useFloating, autoUpdate } from "@floating-ui/vue";
import { vElementHover } from "@vueuse/components";
import DropdownItem from "./DropdownItem.vue.js";
import { DROPDOWN_KEY } from "./type.js";
const _hoisted_1 = { class: "dropdown-scroll" };
const _hoisted_2 = {
  key: 0,
  class: "dropdown-search"
};
const _hoisted_3 = ["placeholder"];
const _hoisted_4 = {
  key: 1,
  class: "dropdown-all-section"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Dropdown",
  props: {
    trigger: { default: "click" },
    showAfter: { default: 100 },
    closeAfter: { default: 100 },
    effect: { default: "light" },
    height: { default: void 0 },
    contentMaxHeight: { default: void 0 },
    maxInlineSize: { default: void 0 },
    searchable: { type: Boolean, default: false },
    searchPlaceholder: { default: "Search" },
    placement: { default: "bottom-start" },
    multiple: { type: Boolean, default: false },
    selectedValues: { default: () => [] },
    items: { default: () => [] },
    showAllItems: { type: Boolean, default: false },
    matchReferenceWidth: { type: Boolean, default: false },
    fullWidth: { type: Boolean, default: false },
    focusOnOpen: { type: Boolean, default: true },
    allItemsLabel: { default: "All items" }
  },
  emits: ["item-selected", "item-toggled", "toggle-all", "search-change", "update:open"],
  setup(__props, { emit: __emit }) {
    const emit = __emit;
    const props = __props;
    const open = ref(false);
    const searchQuery = ref("");
    const searchInputRef = ref(null);
    const reference = ref(null);
    const floating = ref(null);
    const dropdownContentStyle = computed(() => {
      const style = {};
      if (props.height !== void 0) {
        style.height = `${props.height}px`;
      }
      if (props.contentMaxHeight !== void 0) {
        style.maxHeight = typeof props.contentMaxHeight === "number" ? `${props.contentMaxHeight}px` : props.contentMaxHeight;
      }
      return style;
    });
    const getFallbackPlacements = (placement) => {
      const fallbacks = {
        "top-start": ["bottom-start", "top-end", "bottom-end"],
        "top-end": ["bottom-end", "top-start", "bottom-start"],
        top: ["bottom", "top-start", "top-end"],
        "bottom-start": ["top-start", "bottom-end", "top-end"],
        "bottom-end": ["top-end", "bottom-start", "top-start"],
        bottom: ["top", "bottom-start", "bottom-end"]
      };
      return fallbacks[placement] || [];
    };
    const handleMouseEnter = () => {
      if (props.trigger === "hover") {
        setTimeout(() => {
          open.value = true;
        }, props.showAfter);
      }
    };
    const handleMouseLeave = () => {
      if (props.trigger === "click") return open.value = false;
      setTimeout(() => {
        open.value = false;
      }, props.closeAfter);
    };
    const handleClick = () => {
      if (props.trigger === "click") {
        open.value = !open.value;
      }
    };
    const onHover = (state) => {
      if (props.trigger !== "hover") return;
      if (state) return handleMouseEnter();
      return handleMouseLeave();
    };
    const handleItemClick = (item) => {
      emit("item-selected", item);
      open.value = false;
    };
    const handleItemToggle = (item) => {
      emit("item-toggled", item);
    };
    const handleToggleAll = () => {
      const allSelected2 = props.items.every((item) => isItemSelected(item.value));
      emit("toggle-all", { select: !allSelected2 });
    };
    const isItemSelected = (value) => {
      return props.selectedValues.some((v) => JSON.stringify(v) === JSON.stringify(value));
    };
    const handleClickOutside = (event) => {
      if (!open.value) return;
      const target = event.target;
      if (!reference.value || !floating.value) return;
      if (!reference.value.contains(target) && !floating.value.contains(target)) {
        open.value = false;
      }
    };
    const middleware = computed(() => {
      return [
        offset(12),
        flip({
          fallbackPlacements: getFallbackPlacements(props.placement),
          padding: 8
        }),
        shift({
          padding: 8
        }),
        size({
          padding: 8,
          apply({ availableWidth, availableHeight, elements, rects }) {
            Object.assign(elements.floating.style, {
              maxWidth: props.maxInlineSize ? `${props.maxInlineSize}px` : `${availableWidth}px`,
              maxHeight: `${availableHeight}px`,
              ...props.matchReferenceWidth ? {
                width: `${rects.reference.width}px`,
                inlineSize: `${rects.reference.width}px`
              } : {},
              ...props.maxInlineSize ? { maxInlineSize: `${props.maxInlineSize}px` } : {}
            });
          }
        })
      ];
    });
    const dropdownClasses = computed(() => {
      return ["dropdown", `dropdown--${props.effect}`];
    });
    const allSelected = computed(() => {
      if (!props.multiple || !props.items.length) return false;
      return props.items.every((item) => isItemSelected(item.value));
    });
    const someSelected = computed(() => {
      if (!props.multiple || !props.items.length) return false;
      const selectedCount = props.items.filter((item) => isItemSelected(item.value)).length;
      return selectedCount > 0 && selectedCount < props.items.length;
    });
    const { floatingStyles } = useFloating(reference, floating, {
      placement: props.placement,
      middleware,
      whileElementsMounted: autoUpdate,
      strategy: "absolute"
    });
    watch(open, (isOpen) => {
      emit("update:open", isOpen);
      if (isOpen && props.searchable && props.focusOnOpen) {
        nextTick(() => {
          searchInputRef.value?.focus();
        });
      } else {
        searchQuery.value = "";
      }
    });
    watch(searchQuery, (newVal) => {
      emit("search-change", newVal);
    });
    onMounted(() => {
      document.addEventListener("mousedown", handleClickOutside);
    });
    onUnmounted(() => {
      document.removeEventListener("mousedown", handleClickOutside);
    });
    provide(DROPDOWN_KEY, {
      effect: toRef(props, "effect"),
      click: handleItemClick,
      toggle: handleItemToggle,
      multiple: toRef(props, "multiple"),
      selectedValues: toRef(props, "selectedValues"),
      isItemSelected,
      toggleAll: handleToggleAll,
      allSelected,
      someSelected
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, [
        withDirectives((openBlock(), createElementBlock("div", {
          ref_key: "reference",
          ref: reference,
          style: normalizeStyle(__props.fullWidth ? { width: "100%", display: "block" } : {}),
          onClick: handleClick
        }, [
          renderSlot(_ctx.$slots, "default", {}, void 0, true)
        ], 4)), [
          [unref(vElementHover), onHover]
        ]),
        (openBlock(), createBlock(Teleport, { to: "body" }, [
          createVNode(Transition, { name: "dropdown-fade" }, {
            default: withCtx(() => [
              unref(open) ? (openBlock(), createElementBlock("div", {
                key: 0,
                ref_key: "floating",
                ref: floating,
                class: normalizeClass(unref(dropdownClasses)),
                style: normalizeStyle(unref(floatingStyles))
              }, [
                createElementVNode("div", _hoisted_1, [
                  __props.searchable ? (openBlock(), createElementBlock("div", _hoisted_2, [
                    withDirectives(createElementVNode("input", {
                      ref_key: "searchInputRef",
                      ref: searchInputRef,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => isRef(searchQuery) ? searchQuery.value = $event : null),
                      type: "text",
                      placeholder: __props.searchPlaceholder,
                      class: "dropdown-search__input",
                      onClick: _cache[1] || (_cache[1] = withModifiers(() => {
                      }, ["stop"]))
                    }, null, 8, _hoisted_3), [
                      [vModelText, unref(searchQuery)]
                    ])
                  ])) : createCommentVNode("", true),
                  __props.multiple && __props.showAllItems ? (openBlock(), createElementBlock("div", _hoisted_4, [
                    createVNode(DropdownItem, {
                      value: "__all__",
                      "force-show-checkbox": true,
                      "is-all-items": true,
                      onClick: handleToggleAll
                    }, {
                      default: withCtx(() => [
                        createTextVNode(toDisplayString(props.allItemsLabel), 1)
                      ]),
                      _: 1
                    })
                  ])) : createCommentVNode("", true),
                  createElementVNode("div", {
                    class: "dropdown-content",
                    style: normalizeStyle(unref(dropdownContentStyle))
                  }, [
                    renderSlot(_ctx.$slots, "dropdown", { search: unref(searchQuery) }, void 0, true)
                  ], 4)
                ])
              ], 6)) : createCommentVNode("", true)
            ]),
            _: 3
          })
        ]))
      ], 64);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=Dropdown.vue2.js.map
